import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, REQUEST } from '@angular/core';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PortalContextService {
  private static readonly LANTRONIX_ROOT_PORTAL_HOSTS = ['cert.lantronix.com', 'cert-stg.lantronix.com'];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(REQUEST) private request: Request | null
  ) {}

  private isDynamicTenantEnv(): boolean {
    const envName = (environment.name ?? '').toLowerCase();
    return envName === 'staging' || envName === 'stg' || envName === 'production' || envName === 'prod';
  }

  private getFallbackTenantName(): string {
    return (environment.tenantName ?? 'vodafone').trim().toLowerCase() || 'vodafone';
  }

  private normalizeHeaderValue(value: string | null | undefined): string {
    return value?.split(',')[0]?.trim() ?? '';
  }

  private getProtocolFromRequestUrl(url: string | undefined): string {
    if (!url) return '';
    try {
      return new URL(url).protocol.replace(':', '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  private resolveRequestContext(): { host: string; hostname: string; origin: string } {
    if (isPlatformServer(this.platformId) && this.request) {
      const forwardedHost = this.normalizeHeaderValue(this.request.headers.get('x-forwarded-host'));
      const rawHost = (forwardedHost || this.normalizeHeaderValue(this.request.headers.get('host')))
        .toLowerCase()
        .trim();
      const host = rawHost.split(',')[0]?.trim() ?? '';
      const hostname = host.split(':')[0]?.trim() ?? '';
      const forwardedProto = this.normalizeHeaderValue(this.request.headers.get('x-forwarded-proto'))
        .toLowerCase()
        .trim();
      const requestedProtocol = forwardedProto || this.getProtocolFromRequestUrl(this.request.url);
      const protocol = requestedProtocol || (this.isLocalHost(host, hostname) ? 'http' : 'https');
      const origin = host ? `${protocol}://${host}` : '';
      return { host, hostname, origin };
    }

    if (isPlatformBrowser(this.platformId)) {
      const host = window.location.host?.toLowerCase() ?? '';
      const hostname = window.location.hostname?.toLowerCase() ?? '';
      const origin = window.location.origin ?? '';
      return { host, hostname, origin };
    }

    return { host: '', hostname: '', origin: '' };
  }

  private isLocalHost(host: string, hostname: string): boolean {
    return (
      host.includes(':4200') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname.endsWith('.local')
    );
  }

  private isLantronixRootPortalHost(hostname: string): boolean {
    return PortalContextService.LANTRONIX_ROOT_PORTAL_HOSTS.includes(hostname);
  }

  private extractTenantFromHostname(hostname: string): string {
    if (this.isLantronixRootPortalHost(hostname)) {
      return 'lantronix';
    }

    const firstPart = hostname.split('.').find((part) => part.trim().length > 0)?.trim().toLowerCase() ?? '';
    if (!firstPart || firstPart === 'www') {
      return '';
    }
    return firstPart;
  }

  getTenantName(): string {
    const fallbackTenantName = this.getFallbackTenantName();
    if (!this.isDynamicTenantEnv()) {
      return fallbackTenantName;
    }

    const { host, hostname } = this.resolveRequestContext();
    if (!hostname) {
      return fallbackTenantName;
    }

    if (this.isLocalHost(host, hostname)) {
      return fallbackTenantName;
    }

    return this.extractTenantFromHostname(hostname) || fallbackTenantName;
  }

  getPortalNameFromHost(): string {
    return this.getTenantName();
  }

  getApiBaseUrl(): string {
    const configuredBaseUrl = (environment.apiBaseUrl ?? '').trim().replace(/\/+$/, '');
    if (!this.isDynamicTenantEnv()) {
      return configuredBaseUrl;
    }

    const { host, hostname, origin } = this.resolveRequestContext();
    if (origin && !this.isLocalHost(host, hostname)) {
      return origin.replace(/\/+$/, '');
    }

    return configuredBaseUrl;
  }

  resolveApiBaseUrl(apiBaseUrl: string): string {
    const configuredBaseUrl = (apiBaseUrl ?? '').trim().replace(/\/+$/, '');
    if (!this.isDynamicTenantEnv()) {
      return configuredBaseUrl;
    }

    const { host, hostname, origin } = this.resolveRequestContext();
    if (origin && !this.isLocalHost(host, hostname)) {
      return origin.replace(/\/+$/, '');
    }

    return configuredBaseUrl;
  }
}
